import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFetch } from "@/hooks/useFetch";
import { useUI } from "@/context/UIContext";
import { fleetService, vehicleService, geofenceService, alertService } from "@/services/api";
import { PageHeader } from "@/components/common/PageHeader";
import { MapCanvas, MapLegend } from "@/components/common/MapCanvas";
import { VehiclePanel } from "@/components/common/VehiclePanel";
import { SkeletonMap } from "@/components/common/Skeletons";
import { AreaTrend, DonutChart, ChartLegend } from "@/components/common/Charts";
import { relativeTime } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Car, Navigation, Square, Bell, WifiOff, Activity, AlertTriangle } from "lucide-react";

const STRIP = [
  { key: "totalVehicles", label: "Vehicles", icon: Car },
  { key: "active", label: "Active", icon: Activity },
  { key: "moving", label: "Moving", icon: Navigation, accent: "text-emerald-400" },
  { key: "parked", label: "Parked", icon: Square },
  { key: "alerts", label: "Alerts", icon: Bell, accent: "text-primary" },
  { key: "offline", label: "Offline", icon: WifiOff },
];

export default function Dashboard() {
  const navigate = useNavigate();
  const { facilities } = useUI();
  const { data: kpis, loading } = useFetch(() => fleetService.kpis(), []);
  const { data: vehicles, loading: vehiclesLoading } = useFetch(() => vehicleService.list(), []);
  const { data: geofences } = useFetch(() => geofenceService.list(), []);
  const { data: alerts } = useFetch(() => alertService.list({ status: "new" }), []);
  const { data: distanceSeries } = useFetch(() => fleetService.distanceSeries(), []);
  const [selected, setSelected] = useState(null);

  const allVehicles = vehicles || [];
  const allAlerts = alerts || [];
  const movingVehicles = allVehicles.filter((v) => v.status === "moving").slice(0, 5);
  const recentAlerts = [...allAlerts].sort((a, b) => b.time - a.time).slice(0, 4);
  // Only vehicle-status buckets here (not kpis.alerts, which counts open
  // Alert records, a different thing) — these four are exactly the
  // backend's status_counts breakdown and sum to the real vehicle total.
  const statusBreakdown = kpis
    ? [
        { name: "Moving", value: kpis.moving },
        { name: "Parked", value: kpis.parked },
        { name: "Idle", value: kpis.idle },
        { name: "Offline", value: kpis.offline },
      ]
    : [];

  return (
    <div className="p-4 md:p-6 lg:p-8">
      <PageHeader
        title="Fleet Overview"
        subtitle="Real-time visibility across your connected vehicles."
        actions={<Button variant="secondary" onClick={() => navigate("/map")} data-testid="open-live-map">Open Live Map</Button>}
      />

      {/* Command strip */}
      <div className="mb-6 grid grid-cols-3 gap-px overflow-hidden rounded-2xl border border-border/70 bg-border/50 sm:grid-cols-6">
        {STRIP.map((s) => (
          <div key={s.key} className="bg-card/70 p-4 transition hover:bg-card">
            <div className="flex items-center justify-between text-muted-foreground">
              <span className="text-[11px] uppercase tracking-wider">{s.label}</span>
              <s.icon className="h-3.5 w-3.5" />
            </div>
            <div className={cn("mt-2 font-display text-3xl font-semibold tabular-nums", s.accent)}>
              {loading ? <span className="inline-block h-8 w-12 animate-pulse rounded bg-secondary" /> : kpis?.[s.key]}
            </div>
          </div>
        ))}
      </div>

      {/* Map-first command center */}
      <div className="grid gap-4 xl:grid-cols-[1fr_360px]">
        <div className="flex flex-col gap-3">
          <div className="relative h-[420px] md:h-[520px]">
            {vehiclesLoading && !vehicles ? <SkeletonMap /> : (
              <MapCanvas
                testId="dashboard-map"
                viewportKey="dashboard"
                vehicles={allVehicles}
                geofences={geofences || []}
                facilities={facilities}
                selectedId={selected?.id}
                onSelect={setSelected}
              />
            )}
          </div>
          <MapLegend />
        </div>

        {/* Right contextual panel */}
        <div className="h-[420px] md:h-[520px]">
          {selected ? (
            <VehiclePanel vehicle={selected} onClose={() => setSelected(null)} className="h-full" />
          ) : (
            <div className="flex h-full flex-col overflow-hidden rounded-2xl border border-border/70 bg-card/60">
              <div className="border-b border-border/60 p-4">
                <h3 className="font-display text-base font-medium">Fleet Activity</h3>
                <p className="text-xs text-muted-foreground">Select a vehicle on the map for details.</p>
              </div>
              <div className="flex-1 space-y-4 overflow-y-auto p-4 no-scrollbar">
                <div>
                  <div className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">Moving now</div>
                  <div className="space-y-2">
                    {movingVehicles.map((v) => (
                      <button key={v.id} onClick={() => setSelected(v)} className="flex w-full items-center gap-3 rounded-xl border border-border/50 bg-secondary/20 p-2.5 text-left transition hover:border-border hover:bg-secondary/40">
                        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-400/10 text-emerald-400"><Navigation className="h-4 w-4" /></div>
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-medium">{v.name}</div>
                          <div className="truncate text-xs text-muted-foreground">{v.location.address}</div>
                        </div>
                        <div className="text-right"><div className="font-telemetry text-sm">{v.speed}</div><div className="text-[10px] text-muted-foreground">mph</div></div>
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Recent alerts</span>
                    <button onClick={() => navigate("/alerts")} className="text-xs text-primary hover:underline">View all</button>
                  </div>
                  <div className="space-y-2">
                    {recentAlerts.map((a) => {
                      const v = allVehicles.find((x) => x.id === a.vehicleId);
                      return (
                        <button key={a.id} onClick={() => navigate("/alerts")} className="flex w-full items-start gap-2.5 rounded-xl border border-border/50 bg-secondary/20 p-2.5 text-left transition hover:border-border hover:bg-secondary/40">
                          <AlertTriangle className={cn("mt-0.5 h-4 w-4 shrink-0", a.severity === "critical" ? "text-red-400" : a.severity === "warning" ? "text-amber-400" : "text-sky-400")} />
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-sm font-medium">{a.type}</div>
                            <div className="truncate text-xs text-muted-foreground">{v?.name} · {relativeTime(a.time)}</div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Analytics row */}
      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        <Card className="border-border/70 bg-card/60 p-5 lg:col-span-2">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="font-display text-base font-medium">Distance driven</h3>
            <span className="text-xs text-muted-foreground">Last 7 days · km</span>
          </div>
          <AreaTrend data={distanceSeries || []} x="day" y="km" unit="mi" height={220} />
        </Card>
        <Card className="border-border/70 bg-card/60 p-5">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="font-display text-base font-medium">Fleet status</h3>
          </div>
          <DonutChart data={statusBreakdown} centerValue={kpis?.totalVehicles ?? "—"} centerLabel="Total" height={180} />
          <div className="mt-4"><ChartLegend data={statusBreakdown} /></div>
        </Card>
      </div>
    </div>
  );
}
